
import { Observable, combineLatest, of, switchMap, take } from 'rxjs';
import { LogReqHeader, LogReqLineItems, LogReqComments, LogRfqs, LogCatalog, PoHdr, PoLn, PoLnAcct, PoHdrNotes, StdText, LogReqRoutingSections, LogReqStatuses } from '../../../../models';
import * as moment from 'moment-mini';

export class LogQueue {

  static getPLStati(componentId: number, routes: Array<LogReqRoutingSections>) {
    const thisQueue = routes.filter(r => r.componentId == componentId);
    const parkingLotStati = (thisQueue.length > 0 && thisQueue[0].parkingLotStatuses) ? thisQueue[0].parkingLotStatuses.split(',').map(Number) : [];
    return parkingLotStati;
  }

  static filterReqs(reqs, routes, returnActive: boolean, componentId: number): Observable<Array<any[]>> {
    const parkingLotStati = LogQueue.getPLStati(componentId, routes);
    return of(reqs.filter(r => (returnActive) ? !parkingLotStati.includes(r.reqStatusId) : parkingLotStati.includes(r.reqStatusId)));
  }

  static getActiveReqs(reqs: Observable<any[]>, routes: Observable<Array<LogReqRoutingSections>>, componentId: number): Observable<any[]> {
    return combineLatest([reqs,routes]).pipe(
      switchMap(([reqs,routes]) => LogQueue.filterReqs(reqs,routes,true,componentId))
    );
  }

  static getParkingLotReqs(reqs: Observable<any[]>, routes: Observable<Array<LogReqRoutingSections>>, componentId: number): Observable<any[]> {
     return combineLatest([reqs,routes]).pipe(
      switchMap(([reqs,routes]) => LogQueue.filterReqs(reqs,routes,false,componentId))
    );
  }

  static showParkingLot(routes: Observable<Array<LogReqRoutingSections>>, componentId: number): Observable<boolean> {
    return routes.pipe(
      switchMap(routes => {
        const thisQueue = routes.filter(r => r.componentId == componentId);
        return of((thisQueue.length > 0) ? thisQueue[0].showParkReqButton : false);
      }
    ));
  }

  static statusTotals(reqs: Observable<any[]>, componentId: number, routes: Observable<Array<LogReqRoutingSections>>, reqStatuses: Observable<Array<LogReqStatuses>>): Observable<Array<StatusTotal>> {
    return combineLatest([reqs,routes, reqStatuses]).pipe(
      switchMap(([reqs, routes, reqStatuses]) => {
        const plStati = LogQueue.getPLStati(componentId, routes);
        let totals: StatusTotal[] = [];

        plStati.forEach(s =>{
          totals.push(<StatusTotal>{ statusName: reqStatuses.find(stat => stat.reqStatusId == s).reqStatus, total: reqs.map(r => r.reqTotal).reduce((a, b) => a + b, 0)});
        })

        return of(totals);
      })
    );
  }

}

class StatusTotal {
  statusName: string;
  total: number;
}

export class CPPoHdr extends PoHdr {
  constructor(buyerId: string, vendId: string, addrDc: string, req: LogReqHeader, selectedKeys: number[]) {
    super();
    try {
      if (buyerId === undefined || buyerId === null || vendId === undefined || vendId === null || addrDc === undefined || addrDc === null || req === undefined || req === null || req.logReqLineItems.length == 0) {
        throw new Error(`Error attempting to create PO for ${req.reqId} || BuyerID: ${buyerId} | VendorID: ${vendId} | Address: ${addrDc} \n\nReq Details:\n ${JSON.stringify(req)}`);
      }
      else {
        this.buyerId = buyerId;
        this.sPoStatusType = 'O';
        this.fobFld = 'ANC';
        this.ordDt = new Date();
        this.vendId = vendId;
        this.addrDc = addrDc;
        this.rqId = req.reqId;
        this.dueDate = moment().add(req.priority.daysOut,'days').toDate();
        if (req.priority.daysOut <= 7) {
            this.procureTypeCd = 'E';
        }
        else if (req.priority.daysOut <= 14){
            this.procureTypeCd = 'U';
        }
        else {
            this.procureTypeCd = 'R';
        }

        let vendorLines = req.logReqLineItems.filter(l => l.acceptedVendorId == vendId && selectedKeys.indexOf(l.lineItemId) != -1);

        for (let l of vendorLines) {
          this.PoLns.push(new CPPoLn(l));
        }

        for (let t of req.logReqTextCodes) {
          this.TextCds.push({
            textCd: t.textCd,
            textCdRvsnNo: t.textCdRvsnNo
          });
        }

      }
    }
    catch (e) {
      throw e;
    }
  }

  rqId: string;
  dueDate: Date;
  PoLns: Array<CPPoLn> = [];
  TextCds: Array<{
            textCd: string,
            textCdRvsnNo: number
          }> = [];
}

export class CPPoLn extends PoLn {
  constructor(li: LogReqLineItems) {
    super();
    this.poLnNo = li.lineNumber;
    this.ordQty = li.qtyOrdered;
    this.poLnUmCd = li.ui;
    this.poLnDesc = li.nomenclature;
    this.vendPartId = li.vendPartNumber;
    this.netUnitCstAmt = li.acceptedPrice;
    this.poLnExtAmt = li.acceptedPrice * li.qtyOrdered;
    this.rqId = li.lineItemId.toString();
    this.ordDt = new Date();
    this.lineNotes = li.lineNotes;
    this.miscLnChgType = li.miscLnChgType;
    this.indClassCd = li.fsc;
    this.taxableFl = (li.taxable) ? 'Y' : 'N';

    this.PoLnAcct = <PoLnAcct>{
      acctId: li.acctId,
      orgId: '',
      projId: li.projId,
      trnCstAmt: this.poLnExtAmt
    }
  }

  lineNotes: string;
  PoLnAcct: PoLnAcct;
}

export class CatalogItem {
  constructor(lineItem?: LineItem) {
    if (lineItem != undefined) {
      this.catalogId = (lineItem.catalogId == null) ? 0 : lineItem.catalogId;
      this.nomenclature = lineItem.nomenclature;
      this.partNumber = lineItem.partNumber;
      this.manufacturer = lineItem.manufacturer;
      this.vendId = lineItem.acceptedVendorId;
      this.vendorPrice = lineItem.acceptedPrice;
      this.vendorPartNumber = lineItem.vendPartNumber;
    }
  }

  catalogId: number = 0;
  nomenclature: string;
  partNumber: string;
  manufacturer: string;
  catalogCategoryId: number;
  catalogCategory: string;
  catalogSubCategoryId: number;
  vendId: string;
  companyId: number = 14;
  vendorPrice: number;
  vendorPartNumber: string;
  createdOn: Date = new Date();
  vendorName: string;
}

export class OaMapped {
  oanumber: string;
  projectDescription: string;
}

export class Req extends LogReqHeader {
  constructor(user: any) {
    super();
    this.createdById = user.employeeId;
    this.reqCreatedDate = new Date();
    this.requestedById = user.employeeId;
    this.siteId = user.defaultSiteId;
  }
  firstThreeItems: string;
  priorityId: number;
  shippingPriorityId: number;
  equipmentIdOld: string;
  serialNumber: string;
  modelNumber: string;
  copa: string;
  isScheduledMaintenance: boolean;
  workOrderNumber: string;
  holdShipment: boolean = false;
  noPartialShipment: boolean = false;
  rfqOnly: boolean = false;
  reqStatusId: number = 9; //Not Submitted
  isMsi: boolean = false;
  logReqLineItems: Array<LineItem> = [];
}

export class LineItem extends LogReqLineItems {
  constructor(req: Req) {
    super();
    this.reqId = req.reqId;
    this.siteId = req.siteId;
  }

  selected: boolean = false;
  qtyOrdered: number = 1;
  ui: string = "EA ";
  miscLnChgType: string = "MAT";
  acctObj: any = null;
  currentQty: number = null;
}

export class RefDes {
  refDes: string;
  partNumber: string;
  part: string;
  fsc: string;
  niin: string;
  ui: string;
  wuc: string;
  smr: string;
  techOrder: string;
  figure: string;
  index: string;
  errc: string;
  price: number;
}


export class Comment extends LogReqComments {
  constructor(user: any, comment?: string) {
    super();
    this.addedById = user.employeeId;
    this.comment = comment;
    this.commentDate = new Date();
  }

}

export class Bin {
  binAtSiteId: number;
  binNumber: string;
  partDescription: string;
  unitOfIssue: string;
  partNumber: string;
  shopCode: string;
  siteId: number;
  system: string;
  manufacturer: string;
  vendorName: string;
  fsc: string;
  niin: string;
  unitPrice: number;
  errcCode: string;
}

export class RFQ extends LogRfqs {
  constructor(req: LogReqHeader, user: any, companyId: string ) {
    super();
    if (req && req.reqId) {
      this.reqId = req.reqId;
      this.rfqNumber = req.reqId + '-' + (req.logRfqs.length + 1);
    }

    this.employeeId = (user) ? user.employeeId : null;
    this.dateOfInquiry = new Date();
    this.dateRequired = new Date();
    this.company = companyId;
    this.vendor = '';
    this.vendorAddr = '';
  }
  //company = '14';  //DON'T CHANGE THIS, IS FOR COSTPOINT
}


